/** Navigation owns its state and behavior; dependencies are passed in by main. */

const pageSlugs = new Map([
  ['projects', 'projekty'],
  ['about', 'o-nas'],
  ['pricing', 'spolupraca'],
  ['quick-message', 'kontakt'],
  ['privacy', 'ochrana-osobnych-udajov'],
  ['notes', 'clanky'],
]);
const routesBySlug = new Map([...pageSlugs].map(([route, slug]) => [slug, route]));

export function register(app) {
  const { navigation, config, layout, hero, projects, preferences, ui } = app;
  let activeTransition;
  let displayedRoute;
  let revision = 0;
  let activeEntry;
  let returningFromVisit;
  let restoringEntry;
  let nextVisit = 0;
  const positions = new Map();
  function restorePosition(entry) {
    const top = positions.get(entry?.visit) ?? entry?.scrollY ?? 0;
    const snapshot = entry?.archive;
    restoringEntry = null;
    if (!snapshot) {
      window.scrollTo(0, top);
      return;
    }
    function scrollToCard() {
      const panel = document.getElementById(snapshot.panelId);
      if (panel?.hidden || navigation.requestedRoute !== 'projects' || window.history.state?.visit !== entry.visit) return;
      const card = snapshot.cardId && [...(panel?.querySelectorAll('.project-pin') || [])]
        .find(item => item.dataset.projectId === snapshot.cardId);
      if (card) {
        const rect = card.getBoundingClientRect();
        const offset = Math.max(1 - rect.height, Math.min(window.innerHeight - 1, snapshot.cardOffset));
        window.scrollTo(0, window.scrollY + rect.top - offset);
      } else {
        window.scrollTo(0, top);
      }
    }
    const loading = ui.restoreArchiveSnapshot(snapshot);
    if (!loading) {
      scrollToCard();
      return;
    }
    restoringEntry = entry;
    loading.then(ready => {
      if (restoringEntry !== entry) return;
      restoringEntry = null;
      if (ready) scrollToCard();
    });
  }

  function rememberScroll(archiveCard) {
    if (!activeEntry?.visit || restoringEntry?.visit === activeEntry.visit) return;
    const top = window.scrollY;
    positions.set(activeEntry.visit, top);
    const history = window.history;
    // During Back/Forward the browser has already selected the destination entry.
    if (history.state?.visit === activeEntry.visit) {
      const archive = activeEntry.route === 'projects' && ui?.getArchiveSnapshot?.();
      if (archive && archiveCard) {
        archive.cardId = archiveCard.dataset.projectId;
        archive.cardOffset = archiveCard.getBoundingClientRect().top;
      }
      if (archive || history.state.scrollY !== top) {
        history.replaceState({ ...history.state, scrollY: top, ...(archive ? { archive } : {}) }, '');
      }
    }
  }
  navigation.rememberScroll = () => rememberScroll();

  // For browsers without viewport snapshots, fade the content in place. Keep the navigation live.
  function fadeContent(commit, fromOpacity) {
    const surfaces = [document.getElementById('main-content'), document.querySelector('footer')]
      .filter((element) => typeof element?.animate === 'function');
    if (!surfaces.length) {
      commit();
      return;
    }

    let animations = [];
    const cancel = () => animations.forEach((animation) => animation.cancel());
    const fade = (from, to, duration) => {
      animations = surfaces.map((surface) => surface.animate(
        [{ opacity: from }, { opacity: to }],
        { duration, easing: 'ease-in-out', fill: 'both' },
      ));
      return Promise.all(animations.map((animation) => animation.finished));
    };
    const finished = fade(fromOpacity, 0, 160).then(() => {
      if (!commit()) return;
      cancel();
      return fade(0, 1, 230);
    }).finally(cancel);
    return {
      skipTransition: cancel,
      opacity: () => Number(getComputedStyle(surfaces[0]).opacity),
      finished,
    };
  }

  function switchView(route, { instant, pushHistory, scroll = true, archiveCard }, show, revisit) {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (navigation.requestedRoute === route && displayedRoute === route && revisit) {
      revisit(!instant && !reduced);
      return;
    }
    const request = ++revision;
    navigation.requestedRoute = route;
    const fromOpacity = activeTransition?.opacity?.() ?? 1;
    if (activeTransition) {
      activeTransition.skipTransition();
      document.documentElement.classList.remove('capture-route');
      activeTransition = null;
    }
    const commit = () => {
      // A skipped native transition can still invoke its update callback.
      if (request !== revision) return false;
      rememberScroll(archiveCard);
      if (pushHistory) navigation.recordHistoryRoute(route);
      activeEntry = window.history?.state;
      displayedRoute = route;
      show();
      if (scroll) restorePosition(!pushHistory && activeEntry?.route === route ? activeEntry : null);
      const view = document.getElementById(`view-${route}`);
      const title = route === 'home' ? 'Úvod' : view?.querySelector?.('h1, .section-title')?.textContent.trim();
      document.title = title ? `${title} | KUBIS ARCHITEKTI` : 'KUBIS ARCHITEKTI';
      document.dispatchEvent(new Event('kubis:pageview'));
      return true;
    };
    if (displayedRoute === route && revisit) {
      revisit(!instant && !reduced);
      return;
    }
    if (instant || reduced || !displayedRoute) {
      commit();
      return;
    }
    let transition;
    if (typeof document.startViewTransition === 'function') {
      // Only capture the outgoing viewport. The destination, including its sidebar,
      // stays interactive underneath the snapshot throughout its fade.
      const root = document.documentElement;
      root.classList.add('capture-route');
      transition = document.startViewTransition(() => {
        if (request === revision) root.classList.remove('capture-route');
        commit();
      });
    } else {
      transition = fadeContent(commit, fromOpacity);
    }
    if (!transition) return;
    activeTransition = transition;
    transition.ready?.catch(() => {}); // Superseded snapshots are expected to be skipped.
    const release = () => { if (request === revision) activeTransition = null; };
    transition.finished.then(release, release);
  }

  navigation.openMobileMenu = function openMobileMenu() {
    document.body.classList.add('menu-open');
    navigation.menuToggle.setAttribute('aria-expanded', 'true');
    navigation.mobileMenu.setAttribute('aria-hidden', 'false');
    navigation.mobileMenu.inert = false;
  };

  navigation.closeMobileMenu = function closeMobileMenu() {
    document.body.classList.remove('menu-open');
    navigation.menuToggle.setAttribute('aria-expanded', 'false');
    navigation.mobileMenu.setAttribute('aria-hidden', 'true');
    navigation.mobileMenu.inert = true;
  };

  navigation.syncResponsiveMenuState = function syncResponsiveMenuState() {
    const desktopNav = document.querySelector('.desktop-nav');
    if (!desktopNav || getComputedStyle(desktopNav).display === 'none') return;
    if (!document.body.classList.contains('menu-open')) return;
    navigation.closeMobileMenu();
  };

  navigation.hideAllViews = function hideAllViews() {
    navigation.projectLoadRequest = (navigation.projectLoadRequest || 0) + 1;
    projects.pauseActiveShowcase();
    document
      .querySelectorAll('.project-detail-view.active-view video')
      .forEach((video) => video.pause());
    hero.stopHeroAuto();
    if (hero.heroTrack) {
      Array.from(hero.heroTrack.querySelectorAll('video')).forEach((video) => {
        video.pause();
      });
    }
    document
      .querySelectorAll('.page-view, .project-detail-view, .note-detail-view')
      .forEach((view) => {
        view.classList.remove('active-view');
      });
  };

  navigation.routeToHash = function routeToHash(route) {
    if (!route || route === 'home') return '';
    if (route.startsWith('project-')) return `#projekt-${route.slice(8)}`;
    if (route.startsWith('note-')) return `#clanok-${route.slice(5)}`;
    return `#${pageSlugs.get(route)}`;
  };

  navigation.getRouteFromLocation = function getRouteFromLocation() {
    const hash = String(window.location.hash || '')
      .replace(/^#/, '')
      .trim();
    return navigation.normalizeRoute(hash);
  };

  navigation.normalizeRoute = function normalizeRoute(route) {
    route = String(route || 'home').trim();
    route = routesBySlug.get(route) || route;
    if (route.startsWith('projekt-')) route = `project-${route.slice(8)}`;
    if (route === 'project-ka-004' || route === 'project-rd-tekula-interior') route = 'project-rd-tekula';
    if (route.startsWith('clanok-')) route = `note-${route.slice(7)}`;
    if (!config.BLOG_ENABLED && (route === 'notes' || String(route).startsWith('note-')))
      return 'home';
    if (route === 'home') return 'home';
    if (route === 'intake-form' || route === 'contact') return 'quick-message';
    if (route.startsWith('project-') || route.startsWith('note-')) {
      return document.getElementById(`view-${route}`) ? route : 'home';
    }
    return pageSlugs.has(route) && document.getElementById(`view-${route}`) ? route : 'home';
  };

  navigation.recordHistoryRoute = function recordHistoryRoute(route, replace = false) {
    if (!window.history || !window.history.pushState) return;
    const previous = window.history.state;
    const state = previous?.visit && previous.route === route
      ? previous
      : { route, visit: `${Date.now()}-${++nextVisit}`, scrollY: 0 };
    if (!replace && route.startsWith('project-') && displayedRoute === 'projects' && previous?.route === 'projects') {
      state.archiveVisit = previous.visit;
    }
    const nextUrl = `${window.location.pathname}${window.location.search}${navigation.routeToHash(route)}`;
    if (replace || route === navigation.getRouteFromLocation()) history.replaceState(state, '', nextUrl);
    else history.pushState(state, '', nextUrl);
  };

  navigation.closeProjectImageLightboxIfOpen = function closeProjectImageLightboxIfOpen() {
    if (typeof projects.closeProjectImageLightbox !== 'function') return;
    if (
      typeof projects.isProjectImageLightboxOpen === 'function' &&
      !projects.isProjectImageLightboxOpen()
    )
      return;
    projects.closeProjectImageLightbox();
  };

  navigation.navTo = function navTo(page, options = {}) {
    page = navigation.normalizeRoute(page);
    const { pushHistory = true, onComplete = null, instant = false, scroll = true } = options;
    navigation.closeProjectImageLightboxIfOpen();
    const view = document.getElementById(`view-${page}`);
    const isHome = page === 'home';
    // The HTML already displays the opening film while the rest of the page is parsed.
    const openingHome = isHome && !displayedRoute && view.classList.contains('active-view');
    switchView(page, { instant, pushHistory, scroll }, () => {
      if (!openingHome) navigation.hideAllViews();
      view.classList.add('active-view');
      layout.setLayoutMode(isHome ? 'home' : 'subpage');
      const activeLink = page === 'quick-message' ? 'contact' : page;
      layout.setActiveNavLink(isHome || page === 'privacy' ? '' : activeLink);
      navigation.closeMobileMenu();
      if (isHome) hero.restartHeroSequence({ preserveOpening: openingHome });
      layout.queueDotGridZoneUpdate();
      if (typeof onComplete === 'function') onComplete();
    }, (smooth) => {
      navigation.closeMobileMenu();
      if (scroll) window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'instant' });
      if (pushHistory && page !== navigation.getRouteFromLocation()) navigation.recordHistoryRoute(page);
      if (typeof onComplete === 'function') onComplete();
    });
  };

  navigation.goToAbout = function goToAbout(options = {}) {
    navigation.navTo('about', options);
  };

  navigation.backToProjects = function backToProjects() {
    const state = window.history?.state;
    if (state?.archiveVisit && state.route === navigation.getRouteFromLocation()) {
      if (returningFromVisit === state.visit) return;
      returningFromVisit = state.visit;
      window.history.back();
      return;
    }
    navigation.navTo('projects');
  };

  navigation.openContactPath = function openContactPath(kind) {
    navigation.proposalContact(kind === 'project' ? 'private' : 'quick');
  };

  navigation.openNote = function openNote(noteId, options = {}) {
    const { pushHistory = true, instant = false } = options;
    if (!config.BLOG_ENABLED) {
      navigation.navTo('home', {
        pushHistory,
        instant,
      });
      return;
    }
    navigation.closeProjectImageLightboxIfOpen();
    const runOpen = () => {
      navigation.hideAllViews();
      const view = document.getElementById(`view-note-${noteId}`);
      if (view) view.classList.add('active-view');
      layout.setLayoutMode('subpage');
      layout.queueDotGridZoneUpdate();
      layout.setActiveNavLink('notes');
      navigation.closeMobileMenu();
    };
    switchView(`note-${noteId}`, { instant, pushHistory }, runOpen);
  };

  navigation.openProject = function openProject(projectId, options = {}) {
    const { pushHistory = true, instant = false } = options;
    const view = document.getElementById(`view-project-${projectId}`);
    if (!view) {
      navigation.navTo('home', { pushHistory, instant });
      return;
    }
    navigation.closeProjectImageLightboxIfOpen();
    projects.warmShowcaseCover(view);
    const runOpen = () => {
      navigation.hideAllViews();
      view.classList.add('active-view');
      const request = navigation.projectLoadRequest;
      projects
        .ensureShowcase(view)
        .then((showcase) => {
          // A visitor may leave while image dimensions are being read.
          if (request !== navigation.projectLoadRequest || !view.classList.contains('active-view'))
            return;
          projects.activateShowcase(showcase);
          preferences.applyLangTextElements(view);
          layout.queueDotGridZoneUpdate();
        })
        .catch((error) => console.error('Could not prepare project carousel', error));
      layout.setLayoutMode('subpage');
      layout.setActiveNavLink('projects');
      navigation.closeMobileMenu();
      layout.queueDotGridZoneUpdate();
    };
    switchView(`project-${projectId}`, { instant, pushHistory, archiveCard: options.archiveCard }, runOpen);
  };

  navigation.navigateFromHistory = function navigateFromHistory(route, options = {}) {
    route = navigation.normalizeRoute(route);
    const { instant = false } = options;
    if (!route || route === 'home') {
      navigation.navTo('home', {
        pushHistory: false,
        instant,
      });
      return;
    }
    if (route === 'about') {
      navigation.goToAbout({
        pushHistory: false,
        instant,
      });
      return;
    }
    if (route.startsWith('project-')) {
      navigation.openProject(route.replace('project-', ''), {
        pushHistory: false,
        instant,
      });
      return;
    }
    if (route.startsWith('note-')) {
      navigation.openNote(route.replace('note-', ''), {
        pushHistory: false,
        instant,
      });
      return;
    }
    navigation.navTo(route, {
      pushHistory: false,
      instant,
    });
  };

  navigation.followHistoryLocation = function followHistoryLocation() {
    returningFromVisit = null;
    const route = navigation.getRouteFromLocation();
    const history = window.history;
    if (window.location.hash !== navigation.routeToHash(route) || (history && !history.state?.visit)) {
      navigation.recordHistoryRoute(route, true);
    }
    // Both popstate and hashchange can fire for a single traversal.
    if (navigation.requestedRoute !== route) navigation.navigateFromHistory(route);
    else if (displayedRoute === route && history?.state?.visit !== activeEntry?.visit) {
      navigation.rememberScroll();
      activeEntry = history?.state;
      restorePosition(activeEntry);
    }
  };

  navigation.proposalContact = function proposalContact() {
    navigation.navTo('quick-message');
  };
}

/** Initializers are called explicitly, in dependency order, from main. */
export function bindNavigation(app) {
  const { navigation } = app;
  if (window.history && 'scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  window.addEventListener('scrollend', navigation.rememberScroll);
  window.addEventListener('pagehide', navigation.rememberScroll);
  navigation.menuToggle = document.getElementById('menu-toggle');
  navigation.mobileMenu = document.getElementById('mobile-menu');
  navigation.menuToggle.addEventListener('click', () => {
    if (document.body.classList.contains('menu-open')) navigation.closeMobileMenu();
    else navigation.openMobileMenu();
  });
  document.addEventListener('click', (e) => {
    const isOpen = document.body.classList.contains('menu-open');
    if (!isOpen) return;
    const header = document.querySelector('header');
    const clickedInside = navigation.mobileMenu.contains(e.target) || header.contains(e.target);
    if (!clickedInside) navigation.closeMobileMenu();
  });
  window.addEventListener('popstate', navigation.followHistoryLocation);
  window.addEventListener('hashchange', navigation.followHistoryLocation);
}
